import type { PageHeaderProps as ActualPageHeaderProps } from "@replo/design-system/components/PageHeader";

import * as React from "react";

import { DebugModal } from "@editor/components/editor/debug/DebugPanel";
import Flows from "@editor/components/flows/Flows";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useReploHotkeys } from "@editor/hooks/useHotkeys";
import { useEditorDispatch, useEditorSelector } from "@editor/store";
import {
  selectInternalDebugModeOn as selectDevDebugPanelVisibility,
  setDebugPanelVisibility,
} from "@reducers/core-reducer";

import { ToastManager } from "@replo/design-system/components/alert/Toast";
import PageHeader from "@replo/design-system/components/PageHeader";
import { Outlet } from "react-router-dom";

import DashboardSidebar from "./DashboardSidebar";

type SettableLayoutProps = Omit<ActualPageHeaderProps, "children"> & {
  children?: React.ReactNode;
  contentFullWidth?: boolean;
};

interface LayoutContextType {
  setLayout: (props: Partial<SettableLayoutProps>) => void;
}

const LayoutContext = React.createContext<LayoutContextType | undefined>(
  undefined,
);

export const useLayout = () => {
  const context = React.useContext(LayoutContext);
  if (context === undefined) {
    throw new Error("useLayout must be used within a DashboardLayoutProvider");
  }
  return context;
};

export default function DashboardLayout() {
  const dispatch = useEditorDispatch();
  const isDebugPanelVisible = useEditorSelector(selectDevDebugPanelVisibility);
  const toggleDebugMode = React.useCallback(() => {
    dispatch(setDebugPanelVisibility(!isDebugPanelVisible));
  }, [dispatch, isDebugPanelVisible]);
  useReploHotkeys({ debug: toggleDebugMode });

  const { user } = useCurrentUser();
  const params = new URLSearchParams({ utm_source: "dashboard" });

  if (user) {
    params.append("email", user.email);
    if (user.firstName) {
      params.append("name", user.firstName);
    }
  }

  const [pageHeaderProps, setPageHeaderProps] = React.useState<
    Partial<SettableLayoutProps>
  >({});

  const setLayout = React.useCallback((props: Partial<SettableLayoutProps>) => {
    const { contentFullWidth, ...pageHeaderSpecificProps } = props;
    setPageHeaderProps((prevProps) => ({
      ...prevProps,
      ...pageHeaderSpecificProps,
    }));
  }, []);

  return (
    <LayoutContext.Provider value={{ setLayout }}>
      <div className="flex h-screen flex-col">
        <ToastManager />
        {user && <Flows entityType="user" />}
        {isDebugPanelVisible && <DebugModal />}
        <div className="bg-sheet grid w-screen grow grid-cols-[1fr] gap-x-4 overflow-hidden p-3 md:grid-cols-[224px_1fr]">
          <DashboardSidebar />
          <div className="no-scrollbar shadow-selectable border-0.5 border-border overflow-scroll rounded-[10px] bg-white p-6 flex flex-col items-center">
            <PageHeader
              title={pageHeaderProps.title ?? ""}
              description={pageHeaderProps.description}
              actions={
                pageHeaderProps.actions as ActualPageHeaderProps["actions"]
              }
              onBackClick={pageHeaderProps.onBackClick}
            >
              {pageHeaderProps.children}
            </PageHeader>
            <Outlet />
          </div>
        </div>
      </div>
    </LayoutContext.Provider>
  );
}
